import { Link, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { productsBySlug } from "../products";
import Seo from "../components/Seo";

const SITE = "https://olympusroids.com";

// Deterministic 4-char hex hash of a string — used for the verification ID.
function shortHash(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h).toString(16).toUpperCase().padStart(4, "0").slice(0, 4);
}

function verificationId(slug: string): string {
  const part = slug.replace(/-/g, "").toUpperCase();
  return `OM-${part}-${shortHash(slug)}`;
}

export default function Verify() {
  const { slug } = useParams<{ slug: string }>();
  const product = slug ? productsBySlug[slug] : undefined;

  if (!product) {
    return (
      <section className="verify verify--fail">
        <Seo
          title="Unverified — product not found"
          description="This verification code did not match any OLYMPUS MEDS product."
          noindex
        />
        <div className="verify__inner">
          <div className="verify__fail-mark">!</div>
          <h1 className="verify__fail-title">Unverified</h1>
          <p className="verify__fail-sub">
            This code did not match a formulation in the OLYMPUS MEDS catalog. If this product was purchased as
            genuine, please contact us.
          </p>
          <Link to="/catalog" className="btn btn--primary">
            Browse Catalog →
          </Link>
        </div>
      </section>
    );
  }

  const verifyUrl = `${SITE}/verify/${product.slug}`;
  const id = verificationId(product.slug);

  return (
    <section className="verify">
      <Seo
        title={`Authentic — ${product.proprietaryName}`}
        description={`Verified authentic OLYMPUS MEDS product — ${product.proprietaryName} (${product.pharmaceuticalName}). Manufactured in Berlin, Germany.`}
        path={`/verify/${product.slug}`}
        noindex
      />

      <div className="verify__inner">
        <div className="verify__banner">
          <span className="verify__check" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M5 11.5L9.5 16L17 7"
                stroke="currentColor"
                strokeWidth="2.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span>Verified authentic</span>
        </div>

        <h1 className="verify__title">AUTHENTIC</h1>
        <p className="verify__subtitle">
          This product has been verified as an authentic OLYMPUS MEDS formulation.
        </p>

        <div className="verify__card">
          <div className="verify__visual" style={{ background: product.gradient }}>
            {product.image ? (
              <img
                src={product.image}
                alt={`${product.proprietaryName} — ${product.pharmaceuticalName} (${product.strength})`}
                className="verify__image"
              />
            ) : (
              <div className="verify__placeholder">
                <span>{product.proprietaryName.charAt(0)}</span>
              </div>
            )}
          </div>

          <div className="verify__product">
            <div className="verify__seal" aria-label="Official OLYMPUS MEDS seal">
              <svg width="64" height="64" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="1.2" opacity="0.45" />
                <circle cx="24" cy="24" r="17.5" stroke="currentColor" strokeWidth="0.9" />
                <path
                  d="M13 24.5L20.5 32L35 15.5"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="verify__badge">{product.category}</div>
            <h2 className="verify__name">{product.proprietaryName}</h2>
            <div className="verify__pharma">{product.pharmaceuticalName}</div>
            <div className="verify__meta">
              <span>{product.strength}</span>
              <span className="verify__dot">·</span>
              <span>{product.form}</span>
            </div>
            <dl className="verify__attrs">
              <div>
                <dt>Verification ID</dt>
                <dd className="verify__mono">{id}</dd>
              </div>
              <div>
                <dt>Classification</dt>
                <dd>{product.classification}</dd>
              </div>
              <div>
                <dt>Molecular formula</dt>
                <dd className="verify__mono">{product.molecularFormula}</dd>
              </div>
              <div>
                <dt>Manufactured</dt>
                <dd>Berlin, Germany</dd>
              </div>
            </dl>
          </div>
        </div>

        <p className="verify__statement">
          This formulation was produced with EU-sourced active substances, pharmaceutical-grade carriers, and sterile
          cleanroom validation — held to the same standard as every OLYMPUS MEDS product.
        </p>

        <div className="verify__qr-panel">
          <div className="verify__qr-tile">
            <QRCodeSVG value={verifyUrl} size={168} level="M" bgColor="#ffffff" fgColor="#0b0b0f" />
          </div>
          <div className="verify__qr-meta">
            <div className="verify__qr-label">Verification link</div>
            <div className="verify__qr-url">{verifyUrl}</div>
            <div className="verify__qr-hint">Scan this QR from any device to return to this verification page.</div>
          </div>
        </div>

        <div className="verify__actions">
          <Link to={`/product/${product.slug}`} className="btn btn--primary">
            View full product details
            <span className="btn__arrow">→</span>
          </Link>
          <Link to="/catalog" className="btn btn--ghost">
            Browse all formulations
          </Link>
        </div>

        <div className="verify__footnote">
          Verification page is part of the OLYMPUS MEDS reference showcase · Berlin, Germany
        </div>
      </div>
    </section>
  );
}
